import store from 'source/store';
import FormsPageGroup from 'source/pages/root/FormsPageGroup.vue';
import { watch } from 'vue';

let permsChecked = false;

watch(() => store?.state?.auth?.user?.pk, (value) => {
    if (value === undefined || value === null) permsChecked = false;
});

function checkIfUserIsEmployed(to, from, next) {
    const employmentLocked = store.state.auth.user?.user_profile?.is_locked;
    const workforce_id = parseInt(to.params.workforce_id);
    let userWorkforces = store.state.auth.user.user_profile.workforce_ids;
    const isWorkforceValid = !userWorkforces.includes(workforce_id);
    if (to.name === 'forms' && employmentLocked || isWorkforceValid) {
        next({ name: 'access-denied', replace: true });
    } else {
        next();
    }
}

function checkToFormRouteValidity(to, _from, next) {
    const validTabs = ['pending', 'done', 'authored', 'archived', 'missed'];
    const defaultQueries = {
        tab: to.query?.tab ?? 'pending',
        page: to.query?.page ?? 1,
        sort: to.query?.sort ?? 'ascending',
    };

    // Check if at least one query is missing so we can set it to default query
    const needsRedirection = Object.keys(defaultQueries).some(key =>
        to.query?.[key] !== String(defaultQueries[key])
    );

    if (!validTabs.includes(defaultQueries.tab)
        || Object.keys(to.query)?.length > 3
    ) {
        next({ name: 'page-not-found', replace: true });
    } else if (needsRedirection) {
        next({ ...to, query: defaultQueries, replace: true });
    } else {
        next();
    }
}

function assignPrevQueries(to, from) {
    if (from.name === 'forms') {
        to.previousQuery = {
            tab: from.query.tab,
            page: from.query.page,
            sort: from.query.sort,
        };
    }
}

async function checkForm(filters) {
    try {
        return await store.dispatch('genericForm/checkFormPermissionState', filters);
    } catch ({ status }) {
        return await Promise.reject({
            name: status === 404 ? 'page-not-found' : 'access-denied',
            replace: true
        });
    }
}

export default [{
    path: 'forms',
    component: FormsPageGroup,
    beforeEnter: checkIfUserIsEmployed,
    meta: {
        title: 'Forms - HQZen',
        canDisplayGlobalModals: true
    },
    children: [
        {
            path: '',
            name: 'forms',
            component: () => import('source/pages/forms/FormsTable.vue'),
            beforeEnter: checkToFormRouteValidity,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'questions/:form_document_id',
            name: 'form-questions',
            component: () => import('source/pages/forms/FormResponses.vue'),
            beforeEnter: async (to, from, next) => {

                if (permsChecked) {
                    assignPrevQueries(to, from);
                    return next();
                }
                try {
                    const form = await checkForm({
                        pk: to.params.form_document_id,
                        recipients: store.state?.auth?.user?.user_profile.pk,
                        user_action: 'recipient_answering'
                    });
                    if (form?.assignment?.responded) {
                        const { company_id, workforce_id } = to.params;
                        const { pk } = form.assignment;
                        permsChecked = true;
                        return next({
                            name: 'responses-individual',
                            params: {
                                company_id,
                                workforce_id,
                                form_document_assignment_id: pk
                            },
                            query: {
                                recipient_view: true
                            },
                            replace: true
                        });
                    } else {
                        permsChecked = true;
                        /* Remove unwanted queries */
                        return next({ ...to, query: {}, replace: true });
                    }
                } catch (redirect) {
                    return next(redirect);
                }
            },
            props: true,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'responses-individual/:form_document_assignment_id',
            name: 'responses-individual',
            component: () => import('source/pages/forms/FormResponses.vue'),
            beforeEnter: async (to, from, next) => {
                const currentUserPK = store.state?.auth?.user?.user_profile.pk;
                const queryKeys = Object.keys(to.query);

                if (permsChecked) {
                    assignPrevQueries(to, from);
                    return next();
                }

                try {
                    if (queryKeys.some(key => key.toLocaleLowerCase() === 'edit')) {
                        await checkForm({
                            form_document_assignments: to.params.form_document_assignment_id,
                            user_action: 'recipient_viewing',
                            user_action: 'recipient_editing'
                        });
                        permsChecked = true;
                        /* Remove unwanted queries */
                        return next({
                            ...to,
                            query: { edit: true },
                        });
                    } else {
                        const formAssignment = await checkForm({
                            form_document_assignments: to.params.form_document_assignment_id,
                            ...(queryKeys.some(key => key.toLocaleLowerCase() === 'recipient_view') ?
                                { user_action: 'recipient_viewing' } : {}),
                            ...(queryKeys.some(key => key.toLocaleLowerCase() === 'author_view') ?
                                { user_action: 'author_viewing_questions' }: {} )
                        });
                        const { creator_id, assignment: { recipient_id } } = formAssignment;
                        if (((currentUserPK === creator_id)) || (currentUserPK === recipient_id)) {
                            permsChecked = true;
                            /* Remove unwanted queries */
                            return next({
                                ...to,
                                query: { [currentUserPK === recipient_id ? 'recipient_view': 'author_view']: true },
                                replace: true
                            });
                        } else {
                            return next({ name: 'access-denied', replace: true });
                        }
                    }
                } catch (redirect) {
                    return next(redirect);
                }
            },
            props: true,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            // We retain this path to allow older new-response-to-form emails
            // to be properly redirected since the old redirect url is
            // redirect_url = (
            //    f"https://hqzen.com/company/{company_id}/workforce/"
            //    f"{workforce_id}/forms/responses-all/{form_document_id}"
            //) Refer to: send_form_creator_response_summary_email_notification

            path: 'responses-all/:form_document_id',
            redirect: { name: 'form-details', query: { tab: 'questions' } },
            props: true,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'form-details/:form_document_id',
            name: 'form-details',
            component: () => import('source/pages/forms/FormDetails.vue'),
            beforeEnter: async (to, from, next) => {

                if (permsChecked) {
                    assignPrevQueries(to, from);
                    return next();
                }

                try {
                    const queryVals = Object.values(to.query);
                    await checkForm({
                        pk: to.params.form_document_id,
                        user_action: queryVals.some(val => val.toLocaleLowerCase() === 'recipients')
                            ? 'author_viewing_recipients'
                            : 'author_viewing_questions'
                    });
                    permsChecked = true;
                    /* Remove unwanted queries */
                    return next({
                        ...to,
                        query: {
                            ...(queryVals.some(val => val.toLocaleLowerCase() === 'recipients')
                                ? { tab: 'recipients' }
                                : { tab: 'questions' })
                        },
                        replace: true
                    });
                } catch (error) {
                    return next(error);
                }
            },
            props: true,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'update/:form_document_id',
            name: 'update-form',
            component: () => import('source/pages/forms/FormCreateUpdate.vue'),
            beforeEnter: async (to, from, next) => {
                if (!permsChecked) {
                    try {
                        await checkForm({
                            pk: to.params.form_document_id,
                            user_action: 'author_viewing_questions'
                        });
                        /* Remove unwanted queries */
                        permsChecked = true;
                        return next({
                            ...to,
                            query: {},
                        });
                    } catch (redirect) {
                        next(redirect);
                    }
                }
                assignPrevQueries(to, from);
                next();
            },
            props: true,
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            // Shareable forms are listed in the authored tab; created from 'create-form'
            path: 'shareable',
            redirect: { name: 'forms', query: { tab: 'authored', page: 1, sort: 'ascending' } },
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'shareable/:shareable_form_id/edit',
            name: 'update-shareable-form',
            component: () => import('source/pages/forms/shareable/ShareableFormBuilder.vue'),
            props: true,
            meta: {
                title: 'Shareable Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'shareable/:shareable_form_id/responses',
            name: 'shareable-form-responses',
            component: () => import('source/pages/forms/shareable/ShareableFormResponses.vue'),
            props: true,
            beforeEnter: (to, from, next) => {
                assignPrevQueries(to, from);
                next();
            },
            meta: {
                title: 'Shareable Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            // Same path as the recipient view of regular forms, for matching breadcrumbs
            path: 'shareable/responses-individual/:shareable_form_id',
            name: 'shareable-form-answers',
            component: () => import('source/pages/forms/shareable/ShareableFormAnswers.vue'),
            props: true,
            beforeEnter: (to, from, next) => {
                assignPrevQueries(to, from);
                next();
            },
            meta: {
                title: 'Shareable Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
        {
            path: 'create-form',
            name: 'create-form',
            component: () => import('source/pages/forms/FormCreateUpdate.vue'),
            beforeEnter: (to, from, next) => {
                assignPrevQueries(to, from);
                next();
            },
            meta: {
                title: 'Forms - HQZen',
                canDisplayGlobalModals: true,
                isInternal: true
            }
        },
    ]
}];
